// src/Games/WordWizard/WordWizard.js
import { useState, useEffect, useCallback, useRef } from "react";
import "./WordWizard.css";
import wizardImg from "./characters/Magician.png";

const wordBank = {
  Grade1: [
    "frog", "milk", "ship", "tree", "duck", "book", "snow", "fish", "ball", "lamp",
    "door", "star", "nest", "coat", "bike", "rain", "sand", "wind", "hand", "rock"
  ],
  Grade2: [
    "apple", "house", "green", "mouse", "water", "train", "cloud", "grass", "chair", "table",
    "plant", "smile", "stone", "bread", "socks", "boots", "music", "night", "light", "river"
  ],
  Grade3: [
    "planet", "rocket", "school", "bottle", "orange", "purple", "jungle", "window", "bridge", "banana",
    "number", "market", "chicken", "circle", "mirror", "tunnel", "pencil", "friend", "bucket", "candle"
  ],
  Grade4: [
    "magnet", "castle", "energy", "camera", "lizard", "forest", "button", "desert", "travel", "animal",
    "glider", "tablet", "pillow", "cheese", "trophy", "garage", "silver", "motion", "rocket", "gloves"
  ],
  Grade5: [
    "journey", "pirate", "planet", "danger", "shelter", "sunrise", "lighten", "harvest", "protect", "fortune",
    "lantern", "freedom", "respect", "blanket", "pioneer", "machine", "temple", "surface", "flavor", "mystery"
  ],
  Grade6: [
    "diamond", "pattern", "adventure", "history", "justice", "science", "library", "capture", "rescue", "journey",
    "weather", "explore", "compass", "culture", "organic", "contest", "promise", "present", "journey", "subject"
  ],
  Grade7: [
    "language", "question", "mountain", "alphabet", "treasure", "strategy", "triangle", "fraction", "response", "adjective",
    "positive", "negative", "equation", "identity", "describe", "frequent", "exercise", "solution", "cylinder", "narrator"
  ],
};

const levels = Object.keys(wordBank);

function shuffleWord(word) {
  let shuffled = word;
  while (shuffled === word) {
    shuffled = word.split("").sort(() => Math.random() - 0.5).join("");
  }
  return shuffled;
}

function WordWizard() {
  const [stage, setStage] = useState("selectLevel");
  const [currentLevel, setCurrentLevel] = useState(null);
  const [originalWord, setOriginalWord] = useState("");
  const [scrambledWord, setScrambledWord] = useState("");
  const [userInput, setUserInput] = useState("");
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState(3);
  const [timer, setTimer] = useState(15);

  // 用 ref 保存 intervalId，這樣在 callback 也 always 最新
  const intervalIdRef = useRef(null);

  // 這些都要提前宣告，用 useCallback 包起來

  // 下一個單字
  const loadNextWord = useCallback(() => {
    if (!currentLevel) return;
    const words = wordBank[currentLevel];
    const newWord = words[Math.floor(Math.random() * words.length)];
    setOriginalWord(newWord);
    setScrambledWord(shuffleWord(newWord));
    setUserInput("");
    setTimer(15);
  }, [currentLevel]);

  // 扣 HP
  const reduceHP = useCallback(() => {
    setHp(prev => {
      const newHp = prev - 1;
      if (newHp <= 0) {
        setStage("gameOver");
        clearInterval(intervalIdRef.current);
      }
      return newHp;
    });
  }, []);

  // 超時
  const handleTimeOut = useCallback(() => {
    setMessage("⏰ Time's up!");
    reduceHP();
    setTimeout(() => loadNextWord(), 1000);
  }, [reduceHP, loadNextWord]);

  // 選關卡
  const startLevel = useCallback((level) => {
    const words = wordBank[level];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setOriginalWord(randomWord);
    setScrambledWord(shuffleWord(randomWord));
    setCurrentLevel(level);
    setStage("play");
    setUserInput("");
    setMessage("");
    setScore(0);
    setHp(3);
    setTimer(25);
  }, []);

  // 答案判斷
  const handleSubmit = useCallback(() => {
    if (userInput.toLowerCase() === originalWord) {
      clearInterval(intervalIdRef.current);
      setScore(score + 1);
      setMessage("✨ Correct!");
      if (score + 1 >= 5) {
        progressToNextLevel();
      } else {
        setTimeout(() => loadNextWord(), 1000);
      }
    } else {
      setMessage("❌ Try again.");
      reduceHP();
    }
    // eslint-disable-next-line
  }, [userInput, originalWord, score, loadNextWord, progressToNextLevel, reduceHP]);

  // 關卡晉級
  const progressToNextLevel = useCallback(() => {
    const currentIdx = levels.indexOf(currentLevel);
    if (currentIdx < levels.length - 1) {
      const nextLevel = levels[currentIdx + 1];
      setMessage(`🌟 You've unlocked ${nextLevel}!`);
      setTimeout(() => startLevel(nextLevel), 2000);
    } else {
      setStage("result");
    }
  }, [currentLevel, startLevel]);

  // timer 監控
  useEffect(() => {
    if (stage === "play") {
      const id = setInterval(() => {
        setTimer(prev => {
          if (prev === 1) {
            clearInterval(id);
            handleTimeOut();
          }
          return prev - 1;
        });
      }, 1000);
      intervalIdRef.current = id;
      return () => clearInterval(id);
    }
  }, [scrambledWord, stage, handleTimeOut]);

  // -------- 渲染 --------
  return (
    <div className="word-wizard-container">
      <h2 className="word-title">🧙 Word Wizard</h2>

      {stage === "selectLevel" && (
        <div className="level-select">
          <h3>Select Your Grade Level:</h3>
          {levels.map((level) => (
            <button
              key={level}
              className="level-btn"
              onClick={() => startLevel(level)}
            >
              {level}
            </button>
          ))}
        </div>
      )}

      {stage === "play" && (
        <div className="gameplay-box">
          <img src={wizardImg} alt="Wizard" className="wizard-img" />
          <h3>Level: {currentLevel}</h3>
          <p>⏳ Time left: {timer}s</p>
          <p>❤️ HP: {Array.from({ length: hp }, (_, i) => <span key={i}>❤️</span>)}</p>

          <p>Unscramble this word:</p>
          <h2 className="scrambled-word">{scrambledWord}</h2>

          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className="answer-input"
          />
          <button className="submit-btn" onClick={handleSubmit}>
            Submit
          </button>

          <p className="message">{message}</p>
          <p>Score: {score}</p>
        </div>
      )}

      {stage === "gameOver" && (
        <div className="gameover-box">
          <h3>💀 Game Over!</h3>
          <p>You ran out of HP.</p>
          <button className="submit-btn" onClick={() => setStage("selectLevel")}>Try Again</button>
        </div>
      )}

      {stage === "result" && (
        <div className="result-box">
          <h3>🏆 You completed all levels!</h3>
          <p>Great job, Word Wizard!</p>
          <button className="submit-btn" onClick={() => setStage("selectLevel")}>Play Again</button>
        </div>
      )}
    </div>
  );
}

export default WordWizard;
